import React, { FC, useContext, useEffect, useRef } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { LLMID, ModelProvider } from '@/types'
import { IconStarsFilled } from '@tabler/icons-react'

import { CHAT_SETTING_LIMITS } from '@/lib/chat-setting-limits'
import useHotkey from '@/lib/hooks/use-hotkey'
import { cn } from '@/lib/utils'
import { touchChatActivity } from '@/db/chats'
import { ModelSelectChat } from '@/components/models/model-select-chat'
// import { ToolSelect } from "@/components/tools/tool-select"

// Define a mapping of models to tools that should be auto-selected (exact copy from ToolSelect)
const autoSelectToolsMap: Record<string, string[]> = {
  'losi-omega': ['web browsing', 'create images'],
  'losi-mini': ['web browsing', 'create images'],
  'losi-ai': ['web browsing', 'create images'],
  'gpt-4o': ['web browsing', 'create images'],
  'gpt-4o-mini': ['web browsing', 'create images'],
  o1: ['web browsing', 'create images'],
  'o3-mini': ['web browsing', 'create images'],
  'gpt-4.1': ['web browsing', 'create images'],
  'gpt-4.1-mini': ['web browsing', 'create images'],
  'gpt-4.1-nano': ['web browsing', 'create images'],
  'gpt-5': ['web browsing', 'create images'],
  'gpt-5.1': ['web browsing', 'create images'],
  'gpt-5.1-codex-max': ['web browsing', 'create images'],
  'gpt-5.2': ['web browsing', 'create images'],
  'gpt-5.2-chat-latest': ['web browsing', 'create images'],
  'gpt-5.2-pro': ['web browsing', 'create images'],
  'gpt-5-mini': ['web browsing', 'create images'],
  'gpt-5-nano': ['web browsing', 'create images'],
  'claude-3-5-sonnet': ['web browsing', 'create images'],
  'claude-3-opus': ['web browsing', 'create images'],
  'mistral-large': ['web browsing', 'create images'],
  'meta-llama-3-8b': ['web browsing', 'create images'],
  'meta-llama-3-70b': ['web browsing', 'create images']
}

interface ChatSettingsProps {
  className?: string
}

export const ChatSettings: FC<ChatSettingsProps> = ({ className }) => {
  useHotkey('i', () => handleClick())

  const { allModels, platformTools, tools: unsafeTools } = useContext(ChatbotUIContext)

  const { chatSettings, setChatSettings, selectedChat, selectedTools, setSelectedTools } =
    useContext(ChatbotUIChatContext)

  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleClick = () => {
    if (buttonRef.current) {
      buttonRef.current.click()
    }
  }

  useEffect(() => {
    if (!chatSettings) return

    setChatSettings({
      ...chatSettings,
      temperature: Math.min(
        chatSettings.temperature,
        CHAT_SETTING_LIMITS[chatSettings.model]?.MAX_TEMPERATURE || 1
      ),
      contextLength: Math.min(
        chatSettings.contextLength,
        CHAT_SETTING_LIMITS[chatSettings.model]?.MAX_CONTEXT_LENGTH || 4096
      )
    })

    // Auto-select tools based on the selected model (exact copy from ToolSelect)
    const toolsToAutoSelect = autoSelectToolsMap[chatSettings.model]
    if (toolsToAutoSelect && toolsToAutoSelect.length > 0) {
      // Find tool objects that need to be auto-selected
      const toolsToAdd = unsafeTools
        .filter((tool) => toolsToAutoSelect.includes(tool.name?.toLowerCase() || ''))
        .filter((tool) => !selectedTools.some((t) => t.id === tool.id))

      if (toolsToAdd.length > 0) {
        setSelectedTools([...selectedTools, ...toolsToAdd])
      }
    }
  }, [chatSettings?.model, unsafeTools, selectedTools])

  if (!chatSettings) return null

  const handleSelectModel = (modelId: LLMID) => {
    setChatSettings((prev) => {
      return {
        ...prev,
        model: modelId
      }
    })

    // Persist model change to DB immediately for existing chats so
    // background realtime refreshes don't revert the selector.
    if (selectedChat?.id) {
      void touchChatActivity(selectedChat.id, undefined, undefined, modelId)
    }

    // Auto-select tools based on the selected model (exact copy from ToolSelect)
    const toolsToAutoSelect = autoSelectToolsMap[modelId]
    if (toolsToAutoSelect && toolsToAutoSelect.length > 0) {
      // Find tool objects that need to be auto-selected
      const toolsToAdd = unsafeTools
        .filter((tool) => toolsToAutoSelect.includes(tool.name?.toLowerCase() || ''))
        .filter((tool) => !selectedTools.some((t) => t.id === tool.id))

      if (toolsToAdd.length > 0) {
        setSelectedTools([...selectedTools, ...toolsToAdd])
      }
    }
  }

  const selectedModel = allModels.find(
    (x) => x.modelId == chatSettings?.model || x.hostedId == chatSettings?.model
  )

  const pulseAnimation = `
  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
    }
    
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
    }
    
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    }
  }
`

  const PulseIdentifier = () => (
    <div
      style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        animation: 'pulse 2s infinite',
        marginRight: '8px'
      }}
    />
  )

  return (
    <div className={cn('flex items-center space-x-1 md:space-x-2', className)}>
      {/* {selectedModel?.tools && (
        <ToolSelect
          selectedModelId={chatSettings.model}
          selectedTools={selectedTools}
          onSelectTools={setSelectedTools}
        />
      )}  */}
      {/*<ModelSettings*/}
      {/*  chatSettings={chatSettings}*/}
      {/*  onChangeChatSettings={setChatSettings}*/}
      {/*/>*/}
      <ModelSelectChat selectedModelId={chatSettings.model} onSelectModel={handleSelectModel} />
    </div>
    // <Popover>
    //   <PopoverTrigger asChild>
    //     <Button
    //       ref={buttonRef}
    //       className="flex items-center space-x-2"
    //       variant="ghost"
    //     >
    //       <div className="text-lg">
    //         {fullModel?.modelName || chatSettings.model}
    //       </div>
    //
    //       <IconAdjustmentsHorizontal size={28}/>
    //     </Button>
    //   </PopoverTrigger>
    //
    //   <ModelSelect selectedModelId={chatSettings.model} onSelectModel={handleSelectModel} />
    //
    //   {/*<PopoverContent*/}
    //   {/*  // className="bg-background border-input relative flex max-h-[calc(100vh-60px)] w-[300px] flex-col space-y-4 overflow-auto rounded-lg border p-6 sm:w-[350px] md:w-[400px] lg:w-[500px] dark:border-none"*/}
    //   {/*  align="end"*/}
    //   {/*>*/}
    //
    //   {/*</PopoverContent>*/}
    // </Popover>
  )
}
